import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Sparkles, Megaphone, Lightbulb, Copy, Check, Loader2, Rocket, Calendar, Zap, Users, TrendingUp, Target, Star } from 'lucide-react';

export function BoosterMarketingPage() {
  const { api } = useAuth();
  const [promos, setPromos] = useState([]);
  const [types, setTypes] = useState([]);
  const [canaux, setCanaux] = useState([]);
  const [conseils, setConseils] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingConseils, setLoadingConseils] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [generatedTextes, setGeneratedTextes] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const [form, setForm] = useState({
    titre: '', description: '', type: '', reduction: '',
    date_debut: '', date_fin: '', canaux: [],
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, tRes] = await Promise.all([
        api.get('/marketing/promotions'),
        api.get('/marketing/types'),
      ]);
      setPromos(pRes.data);
      setTypes(tRes.data.types || []);
      setCanaux(tRes.data.canaux || []);
    } catch {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditItem(null);
    setForm({ titre: '', description: '', type: '', reduction: '', date_debut: '', date_fin: '', canaux: [] });
    setGeneratedTextes(null);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditItem(p);
    setForm({ titre: p.titre, description: p.description, type: p.type, reduction: p.reduction, date_debut: p.date_debut, date_fin: p.date_fin, canaux: p.canaux || [] });
    setGeneratedTextes(p.textes_generes || null);
    setDialogOpen(true);
  };

  const toggleCanal = (id) => {
    setForm(f => ({
      ...f,
      canaux: f.canaux.includes(id) ? f.canaux.filter(c => c !== id) : [...f.canaux, id]
    }));
  };

  const handleGenerateTextes = async () => {
    if (!form.titre) { toast.error('Ajoutez un titre'); return; }
    setGenerating(true);
    try {
      const res = await api.post('/marketing/generate-textes', form);
      setGeneratedTextes(res.data.textes);
      toast.success('Textes generes !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur generation');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!form.titre || !form.date_debut) {
      toast.error('Titre et date de debut requis');
      return;
    }
    try {
      const payload = { ...form, textes_generes: generatedTextes || {} };
      if (editItem) {
        await api.put(`/marketing/promotions/${editItem.id}`, payload);
        toast.success('Promotion mise a jour');
      } else {
        await api.post('/marketing/promotions', payload);
        toast.success('Promotion creee');
      }
      setDialogOpen(false);
      fetchData();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/marketing/promotions/${id}`);
      toast.success('Supprimee');
      fetchData();
    } catch {
      toast.error('Erreur');
    }
  };

  const loadConseils = async () => {
    setLoadingConseils(true);
    try {
      const res = await api.get('/marketing/conseils-ia');
      setConseils(res.data.conseils || []);
      toast.success('Conseils generes !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur generation conseils');
    } finally {
      setLoadingConseils(false);
    }
  };

  const copyText = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copie !');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const activePromos = promos.filter(p => p.statut === 'active');
  const plannedPromos = promos.filter(p => p.statut === 'planifiee');
  const pastPromos = promos.filter(p => p.statut === 'terminee');

  const catIcons = { frequentation: Users, panier: TrendingUp, social: Megaphone, fidelite: Star, uber: Rocket, viral: Zap };
  const catColors = { frequentation: 'border-blue-300 bg-blue-50', panier: 'border-green-300 bg-green-50', social: 'border-pink-300 bg-pink-50', fidelite: 'border-amber-300 bg-amber-50', uber: 'border-emerald-300 bg-emerald-50', viral: 'border-purple-300 bg-purple-50' };
  const impactBadge = { fort: 'bg-red-100 text-red-700', moyen: 'bg-amber-100 text-amber-700', rapide: 'bg-blue-100 text-blue-700' };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6" data-testid="marketing-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Rocket className="w-6 h-6 text-primary" /> Booster Marketing
          </h1>
          <p className="text-muted-foreground text-sm">Creez des promotions virales et boostez votre frequentation</p>
        </div>
        <Button onClick={openCreate} data-testid="new-promo-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle promotion
        </Button>
      </div>

      <Tabs defaultValue="promos">
        <TabsList>
          <TabsTrigger value="promos" data-testid="tab-promos">
            <Megaphone className="w-4 h-4 mr-1" /> Promotions ({promos.length})
          </TabsTrigger>
          <TabsTrigger value="conseils" data-testid="tab-conseils">
            <Lightbulb className="w-4 h-4 mr-1" /> Conseils IA
          </TabsTrigger>
        </TabsList>

        {/* PROMOTIONS TAB */}
        <TabsContent value="promos" className="space-y-6">
          {/* Active promos */}
          {activePromos.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Zap className="w-5 h-5 text-green-600" /> En cours ({activePromos.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {activePromos.map(p => <PromoCard key={p.id} promo={p} onEdit={openEdit} onDelete={handleDelete} types={types} />)}
              </div>
            </div>
          )}

          {/* Planned */}
          {plannedPromos.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" /> Planifiees ({plannedPromos.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {plannedPromos.map(p => <PromoCard key={p.id} promo={p} onEdit={openEdit} onDelete={handleDelete} types={types} />)}
              </div>
            </div>
          )}

          {/* Past */}
          {pastPromos.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-muted-foreground">Terminees ({pastPromos.length})</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pastPromos.map(p => <PromoCard key={p.id} promo={p} onEdit={openEdit} onDelete={handleDelete} types={types} muted />)}
              </div>
            </div>
          )}

          {promos.length === 0 && (
            <Card>
              <CardContent className="py-16 text-center">
                <Megaphone className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Aucune promotion</h3>
                <p className="text-muted-foreground mb-4">Creez votre premiere promotion et generez des textes marketing viraux avec l'IA</p>
                <Button onClick={openCreate}><Plus className="w-4 h-4 mr-2" /> Creer une promotion</Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* CONSEILS IA TAB */}
        <TabsContent value="conseils" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> Conseils IA pour tripler votre frequentation</CardTitle>
                  <CardDescription>L'IA analyse vos donnees et vous propose des actions concretes</CardDescription>
                </div>
                <Button onClick={loadConseils} disabled={loadingConseils} data-testid="generate-conseils-btn">
                  {loadingConseils ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  {conseils.length > 0 ? 'Regenerer' : 'Generer les conseils'}
                </Button>
              </div>
            </CardHeader>
            {loadingConseils && (
              <CardContent>
                <div className="flex flex-col items-center gap-3 py-12">
                  <Loader2 className="w-10 h-10 animate-spin text-primary" />
                  <p className="text-muted-foreground">L'IA analyse votre restaurant...</p>
                </div>
              </CardContent>
            )}
          </Card>

          {conseils.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {conseils.map((c, i) => {
                const Icon = catIcons[c.categorie] || Target;
                const colorCls = catColors[c.categorie] || 'border-muted bg-muted/30';
                return (
                  <Card key={i} className={`border-2 ${colorCls}`} data-testid={`conseil-ia-${i}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Icon className="w-5 h-5 flex-shrink-0" />
                          <CardTitle className="text-base">{c.titre}</CardTitle>
                        </div>
                        <Badge className={impactBadge[c.impact] || 'bg-slate-100'}>{c.impact}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="space-y-1.5">
                        {(c.etapes || []).map((etape, j) => (
                          <div key={j} className="flex items-start gap-2 text-sm">
                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-bold mt-0.5">{j+1}</span>
                            <span>{etape}</span>
                          </div>
                        ))}
                      </div>
                      {c.estimation_impact && (
                        <>
                          <Separator />
                          <p className="text-xs font-medium text-primary flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" /> {c.estimation_impact}
                          </p>
                        </>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* CREATE/EDIT PROMO DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="promo-dialog">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Modifier' : 'Nouvelle'} promotion</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Titre de la promotion</Label>
                <Input value={form.titre} onChange={e => setForm(f => ({...f, titre: e.target.value}))}
                  placeholder="Ex: Happy Hour Bubble Tea -30%" data-testid="promo-titre" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Description</Label>
                <Input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                  placeholder="Ex: Tous les bubble tea a -30% entre 14h et 16h" data-testid="promo-description" />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={v => setForm(f => ({...f, type: v}))}>
                  <SelectTrigger data-testid="promo-type"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>
                    {types.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reduction / Offre</Label>
                <Input value={form.reduction} onChange={e => setForm(f => ({...f, reduction: e.target.value}))}
                  placeholder="Ex: -30%, 1 offert, menu a 9.90EUR" data-testid="promo-reduction" />
              </div>
              <div className="space-y-2">
                <Label>Date debut</Label>
                <Input type="date" value={form.date_debut} onChange={e => setForm(f => ({...f, date_debut: e.target.value}))} data-testid="promo-date-debut" />
              </div>
              <div className="space-y-2">
                <Label>Date fin</Label>
                <Input type="date" value={form.date_fin} onChange={e => setForm(f => ({...f, date_fin: e.target.value}))} data-testid="promo-date-fin" />
              </div>
            </div>

            {/* Canaux */}
            <div className="space-y-2">
              <Label>Canaux de diffusion</Label>
              <div className="flex flex-wrap gap-2">
                {canaux.map(c => (
                  <button key={c.id} type="button"
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${form.canaux.includes(c.id) ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/50 border-muted hover:border-primary/50'}`}
                    onClick={() => toggleCanal(c.id)} data-testid={`canal-${c.id}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <Separator />

            {/* AI Generation */}
            <div className="space-y-3">
              <Button onClick={handleGenerateTextes} disabled={generating || !form.titre}
                variant="outline" className="w-full gap-2 border-primary/30 hover:bg-primary/5" data-testid="generate-textes-btn">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-amber-500" />}
                {generating ? 'Generation en cours...' : 'Generer les textes marketing avec l\'IA'}
              </Button>

              {generatedTextes && (
                <div className="space-y-3 p-4 bg-muted/30 rounded-lg border" data-testid="generated-textes">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-500" /> Textes generes
                  </h4>
                  {Object.entries(generatedTextes).map(([canal, content]) => {
                    if (canal === 'raw') return <div key={canal} className="text-sm whitespace-pre-wrap">{content}</div>;
                    return (
                      <div key={canal} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className="capitalize">{canal.replace('_', ' ')}</Badge>
                          <Button size="sm" variant="ghost" className="h-7 text-xs"
                            onClick={() => copyText(typeof content === 'string' ? content : JSON.stringify(content, null, 2), canal)}>
                            {copiedField === canal ? <Check className="w-3 h-3 mr-1 text-green-600" /> : <Copy className="w-3 h-3 mr-1" />}
                            {copiedField === canal ? 'Copie !' : 'Copier'}
                          </Button>
                        </div>
                        <div className="text-sm bg-background p-3 rounded border space-y-1">
                          {typeof content === 'string' ? (
                            <p className="whitespace-pre-wrap">{content}</p>
                          ) : (
                            Object.entries(content).map(([k, v]) => (
                              <div key={k}>
                                <span className="text-xs font-medium text-muted-foreground uppercase">{k} :</span>
                                <p className="whitespace-pre-wrap">{v}</p>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Annuler</Button></DialogClose>
            <Button onClick={handleSave} data-testid="save-promo-btn">
              {editItem ? 'Mettre a jour' : 'Creer la promotion'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PromoCard({ promo, onEdit, onDelete, types, muted }) {
  const typeObj = types.find(t => t.id === promo.type);
  const statusColors = { active: 'bg-green-100 text-green-700', planifiee: 'bg-blue-100 text-blue-700', terminee: 'bg-slate-100 text-slate-500' };
  const statusLabels = { active: 'En cours', planifiee: 'Planifiee', terminee: 'Terminee' };

  return (
    <Card className={`transition-all hover:shadow-md ${muted ? 'opacity-60' : ''}`} data-testid={`promo-card-${promo.id}`}>
      <CardContent className="pt-5 pb-4 space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Badge className={statusColors[promo.statut] || ''}>{statusLabels[promo.statut] || promo.statut}</Badge>
              {typeObj && <Badge variant="outline" className="text-xs">{typeObj.label}</Badge>}
            </div>
            <h3 className="font-semibold text-lg">{promo.titre}</h3>
            {promo.description && <p className="text-sm text-muted-foreground">{promo.description}</p>}
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(promo)} data-testid={`edit-promo-${promo.id}`}>
              <Pencil className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onDelete(promo.id)} data-testid={`delete-promo-${promo.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm">
          {promo.reduction && (
            <span className="font-bold text-primary">{promo.reduction}</span>
          )}
          <span className="text-muted-foreground">
            {promo.date_debut} → {promo.date_fin || '...'}
          </span>
        </div>
        {promo.canaux?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {promo.canaux.map(c => (
              <Badge key={c} variant="secondary" className="text-xs capitalize">{c.replace('_', ' ')}</Badge>
            ))}
          </div>
        )}
        {promo.textes_generes && Object.keys(promo.textes_generes).length > 0 && (
          <div className="flex items-center gap-1 text-xs text-amber-600">
            <Sparkles className="w-3 h-3" /> Textes IA generes ({Object.keys(promo.textes_generes).length} canaux)
          </div>
        )}
      </CardContent>
    </Card>
  );
}
